/**
 * SUSI — core runtime.
 *
 * Loaded (deferred) on every page before any component script. It only holds
 * shared helpers; every feature lives in its own custom element file.
 *
 * Public surface (all on window.theme):
 *   emit, debounce, throttle, isRTL, mediaMatches, define, whenDefined,
 *   fetchDocument, clearFetchCache, fetchSections, replaceFrom, formatMoney,
 *   trapFocus, releaseFocus, lockScroll, unlockScroll,
 *   loadScript, loadStyle, loadImages, onVisible, pauseAllMedia, scrollToElement,
 *   stickyHeaderHeight, cart.{get,add,change,update,refresh,current}, cartState
 *
 * Custom events (see assets/custom.js for the full reference):
 *   on:debounced-resize, on:breakpoint-change, on:cart:add, on:cart:change,
 *   on:cart:error
 */
(() => {
  const theme = (window.theme = window.theme || {});
  const root = document.documentElement;

  /* ---------------------------------------------------------------------- */
  /* Small helpers                                                           */
  /* ---------------------------------------------------------------------- */

  /**
   * Dispatches a bubbling CustomEvent and returns it.
   * @param {string} name - Event name.
   * @param {object} [detail] - Event detail.
   * @param {EventTarget} [target] - Defaults to document.
   * @param {object} [options] - { cancelable: boolean }
   */
  theme.emit = (name, detail = {}, target = document, options = {}) => {
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: Boolean(options.cancelable),
      detail
    });
    target.dispatchEvent(event);
    if (window.Shopify && Shopify.designMode && /^(on|dispatch):/.test(name)) {
      // eslint-disable-next-line no-console
      console.debug(`[SUSI] ${name}`, detail);
    }
    return event;
  };

  theme.debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  theme.throttle = (fn, wait = 100) => {
    let last = 0;
    let timer;
    return (...args) => {
      const now = Date.now();
      const remaining = wait - (now - last);
      clearTimeout(timer);
      if (remaining <= 0) {
        last = now;
        fn.apply(null, args);
      } else {
        timer = setTimeout(() => {
          last = Date.now();
          fn.apply(null, args);
        }, remaining);
      }
    };
  };

  theme.isRTL = () => root.dir === 'rtl';

  /** Registers a custom element once, even if its script is included twice. */
  theme.define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  theme.whenDefined = (name) => customElements.whenDefined(name);

  /* ---------------------------------------------------------------------- */
  /* Viewport state                                                          */
  /* ---------------------------------------------------------------------- */

  theme.mediaQueries = Object.assign(
    { md: '(min-width: 768px)', lg: '(min-width: 1000px)', xl: '(min-width: 1300px)' },
    theme.mediaQueries || {}
  );
  theme.mediaMatches = {};

  Object.entries(theme.mediaQueries).forEach(([key, query]) => {
    const list = window.matchMedia(query);
    theme.mediaMatches[key] = list.matches;
    list.addEventListener('change', (event) => {
      theme.mediaMatches[key] = event.matches;
      theme.emit('on:breakpoint-change', {}, window);
    });
  });

  const setViewportVars = () => {
    root.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    root.style.setProperty('--scrollbar-width', `${window.innerWidth - root.clientWidth}px`);
  };

  let lastWidth = window.innerWidth;
  window.addEventListener(
    'resize',
    theme.debounce(() => {
      setViewportVars();
      // Mobile browsers fire resize when the URL bar collapses; ignore those.
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      theme.emit('on:debounced-resize', {}, window);
    }, 300)
  );
  setViewportVars();

  /** Height of the header when it is sticky, otherwise 0. */
  theme.stickyHeaderHeight = () => {
    const header = document.querySelector('[data-sticky-header]');
    return header ? Math.ceil(header.getBoundingClientRect().height) : 0;
  };

  /* ---------------------------------------------------------------------- */
  /* Fetching + DOM replacement                                              */
  /* ---------------------------------------------------------------------- */

  const documentCache = new Map();

  /** Forgets cached documents (call after anything that changes rendered product/cart HTML). */
  theme.clearFetchCache = () => documentCache.clear();

  /**
   * Fetches a URL and resolves with a parsed Document.
   * @param {string} url
   * @param {object} [options] - { cache: boolean, signal: AbortSignal }
   */
  theme.fetchDocument = async (url, options = {}) => {
    if (options.cache && documentCache.has(url)) {
      return new DOMParser().parseFromString(documentCache.get(url), 'text/html');
    }
    const response = await fetch(url, { signal: options.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const text = await response.text();
    if (options.cache) documentCache.set(url, text);
    return new DOMParser().parseFromString(text, 'text/html');
  };

  /**
   * Uses the Section Rendering API. Resolves with { [sectionId]: htmlString }.
   * @param {string[]} sectionIds
   * @param {string} [url] - Page to render the sections for. Defaults to the current page.
   */
  theme.fetchSections = async (sectionIds, url = window.location.href, options = {}) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('sections', sectionIds.join(','));
    const response = await fetch(target.toString(), { signal: options.signal });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  };

  /**
   * Replaces the contents of elements in the live page with their counterparts
   * from another document / html string.
   * @param {Document|string} source
   * @param {string[]} selectors - Each must match in both documents.
   * @param {ParentNode} [scope] - Where to look for the live elements.
   */
  theme.replaceFrom = (source, selectors, scope = document) => {
    const doc = typeof source === 'string' ? new DOMParser().parseFromString(source, 'text/html') : source;
    selectors.forEach((selector) => {
      const fresh = doc.querySelector(selector);
      const current = scope.querySelector(selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Money                                                                   */
  /* ---------------------------------------------------------------------- */

  /**
   * Formats an amount in minor units using a Shopify money format string.
   * @param {number|string} cents
   * @param {string} [format] - Defaults to theme.settings.moneyFormat.
   */
  theme.formatMoney = (cents, format) => {
    const template = format || (theme.settings && theme.settings.moneyFormat) || '${{amount}}';
    const value = typeof cents === 'string' ? Number(cents.replace('.', '')) : cents;

    const withDelimiters = (number, precision, thousands, decimal) => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };

    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(value, 0, ' ', ',');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return withDelimiters(value, 2, ' ', '.');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Focus + scroll management                                               */
  /* ---------------------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex^="-"])';
  let activeTrap = null;

  const visibleFocusables = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (element) => element.offsetParent !== null || element.getClientRects().length
    );

  /**
   * Keeps keyboard focus inside a container until releaseFocus is called.
   * @param {HTMLElement} container
   * @param {HTMLElement} [initialFocus] - Defaults to the container itself.
   */
  theme.trapFocus = (container, initialFocus) => {
    theme.releaseFocus();
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = visibleFocusables(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeydown);
    activeTrap = { container, onKeydown };
    const target = initialFocus || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  /** @param {HTMLElement} [returnTo] - Element to focus after releasing. */
  theme.releaseFocus = (returnTo) => {
    if (activeTrap) {
      activeTrap.container.removeEventListener('keydown', activeTrap.onKeydown);
      activeTrap = null;
    }
    if (returnTo && typeof returnTo.focus === 'function') returnTo.focus({ preventScroll: true });
  };

  let scrollLocks = 0;
  theme.lockScroll = () => {
    scrollLocks += 1;
    if (scrollLocks === 1) {
      setViewportVars();
      root.classList.add('scroll-locked');
    }
  };

  theme.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) root.classList.remove('scroll-locked');
  };

  /**
   * Smoothly scrolls so an element sits below the sticky header.
   * @param {Element} element
   * @param {number} [offset] - Extra gap in px.
   */
  theme.scrollToElement = (element, offset = 20) => {
    const top = element.getBoundingClientRect().top + window.scrollY - theme.stickyHeaderHeight() - offset;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  /* ---------------------------------------------------------------------- */
  /* Lazy loading                                                            */
  /* ---------------------------------------------------------------------- */

  const assetPromises = new Map();

  theme.loadScript = (src) => {
    if (!assetPromises.has(src)) {
      assetPromises.set(
        src,
        new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        })
      );
    }
    return assetPromises.get(src);
  };

  theme.loadStyle = (href) => {
    if (!assetPromises.has(href)) {
      assetPromises.set(
        href,
        new Promise((resolve, reject) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = resolve;
          link.onerror = reject;
          document.head.appendChild(link);
        })
      );
    }
    return assetPromises.get(href);
  };

  /**
   * Runs a callback the first time (or every time) an element nears the viewport.
   * @param {Element} element
   * @param {Function} callback - Receives the IntersectionObserverEntry.
   * @param {object} [options] - { rootMargin: string, threshold: number, once: boolean }
   * @returns {IntersectionObserver}
   */
  theme.onVisible = (element, callback, options = {}) => {
    const { rootMargin = '200px 0px', threshold = 0, once = true } = options;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (once) observer.disconnect();
          callback(entry);
        });
      },
      { rootMargin, threshold }
    );
    observer.observe(element);
    return observer;
  };

  /**
   * Hydrates images rendered with `loading: 'manual'` (data-src / data-srcset) inside a scope.
   * Call it when a panel that holds such images is first shown.
   */
  theme.loadImages = (scope = document) => {
    scope.querySelectorAll('img[data-src], img[data-srcset]').forEach((img) => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    });
  };

  /** Pauses native, YouTube and Vimeo media inside a scope. */
  theme.pauseAllMedia = (scope = document) => {
    scope.querySelectorAll('video').forEach((video) => {
      if (!video.hasAttribute('data-background')) video.pause();
    });
    scope.querySelectorAll('iframe[src*="youtube"]').forEach((frame) => {
      frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    });
    scope.querySelectorAll('iframe[src*="vimeo"]').forEach((frame) => {
      frame.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    scope.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Cart API                                                                */
  /* ---------------------------------------------------------------------- */
  /*
   * Every mutation goes through here so the whole theme reacts in one way:
   *
   *   on:cart:add     { variantId }                 a variant entered the cart for the first time
   *   on:cart:change  { source, cart, response, items?, opener? }
   *                   source: 'add' | 'change' | 'update' | 'refresh'
   *                   cart:   the full, fresh cart object
   *   on:cart:error   { error }                     a request was rejected
   *
   * Listeners (cart drawer, cart page, header count, added notification) live
   * in assets/cart.js and friends. Callers never refresh UI themselves.
   */

  const routes = theme.routes || {};
  const cartState = (theme.cartState = Object.assign({ itemCount: 0, variantIds: [] }, theme.cartState || {}));

  const rememberCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    cartState.itemCount = cart.item_count;
    cartState.variantIds = cart.items.map((item) => item.variant_id);
    theme.cart.current = cart;
    return cart;
  };

  const withSections = (body, options) => {
    if (!options.sections || !options.sections.length) return body;
    const sections = options.sections.join(',');
    const sectionsUrl = options.sectionsUrl || window.location.pathname;
    if (body instanceof FormData) {
      body.set('sections', sections);
      body.set('sections_url', sectionsUrl);
    } else {
      body.sections = sections;
      body.sections_url = sectionsUrl;
    }
    return body;
  };

  const cartRequest = async (url, body) => {
    const isFormData = body instanceof FormData;
    const init = {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body
    };
    if (!isFormData) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const response = await fetch(url, init);
    const data = await response.json();
    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Cart error';
      theme.emit('on:cart:error', { error: message });
      const error = new Error(message);
      error.data = data;
      throw error;
    }
    return data;
  };

  theme.cart = {
    /** Last cart object seen by the theme (null until the first request). */
    current: null,

    /** Resolves with the cart object. */
    get: async () => {
      const response = await fetch(`${(routes.cart || '/cart').replace(/\/$/, '')}.js`, {
        headers: { Accept: 'application/json' }
      });
      return rememberCart(await response.json());
    },

    /**
     * Adds to the cart.
     * @param {FormData|object} payload - A product form's FormData, or { items: [...] } / { id, quantity }.
     * @param {object} [options] - { sections: string[], sectionsUrl: string, opener: Element, silent: boolean }
     *   opener: element to return focus to if a drawer opens. silent: skip on:cart:change.
     * @returns {Promise<object>} Response JSON (includes `sections` when requested).
     */
    add: async (payload, options = {}) => {
      const known = cartState.variantIds.slice();
      const body = payload instanceof FormData ? payload : Object.assign({}, payload);
      const data = await cartRequest(routes.cartAdd || '/cart/add.js', withSections(body, options));
      const items = data.items || [data];
      const cart = await theme.cart.get();
      items.forEach((item) => {
        if (!known.includes(item.variant_id)) theme.emit('on:cart:add', { variantId: item.variant_id });
      });
      if (!options.silent) {
        theme.emit('on:cart:change', { source: 'add', response: data, cart, items, opener: options.opener || null });
      }
      return data;
    },

    /**
     * Changes one line.
     * @param {object} payload - { line | id, quantity, properties? }
     * @param {object} [options] - { sections: string[], sectionsUrl: string, silent: boolean }
     * @returns {Promise<object>} The cart (plus `sections` when requested).
     */
    change: async (payload, options = {}) => {
      const url = routes.cartChange || `${(routes.cart || '/cart').replace(/\/$/, '')}/change.js`;
      const data = await cartRequest(url, withSections(Object.assign({}, payload), options));
      rememberCart(data);
      if (!options.silent) theme.emit('on:cart:change', { source: 'change', response: data, cart: data });
      return data;
    },

    /**
     * Updates the note, attributes or several quantities at once.
     * @param {object} payload - { note?, attributes?, updates? }
     * @param {object} [options] - { sections: string[], sectionsUrl: string, silent: boolean }
     * @returns {Promise<object>} The cart (plus `sections` when requested).
     */
    update: async (payload, options = {}) => {
      const data = await cartRequest(routes.cartUpdate || '/cart/update.js', withSections(Object.assign({}, payload), options));
      rememberCart(data);
      if (!options.silent) theme.emit('on:cart:change', { source: 'update', response: data, cart: data });
      return data;
    },

    /** Re-reads the cart and tells every listener to redraw (use after third party changes). */
    refresh: async (detail = {}) => {
      const cart = await theme.cart.get();
      theme.emit('on:cart:change', Object.assign({ source: 'refresh', cart }, detail));
      return cart;
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Global link behaviour                                                   */
  /* ---------------------------------------------------------------------- */

  const settings = theme.settings || {};

  const prepareExternalLinks = (scope = document) => {
    if (!settings.externalLinksNewTab) return;
    scope.querySelectorAll('a[href^="http"]:not([target])').forEach((link) => {
      if (link.hostname && link.hostname !== window.location.hostname) {
        link.target = '_blank';
        link.rel = 'noopener';
      }
    });
  };

  document.addEventListener('click', (event) => {
    if (!settings.internalLinksSmoothScroll) return;
    const link = event.target.closest('a[href^="#"]');
    if (!link || link.hash.length < 2 || link.hasAttribute('data-no-scroll')) return;
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    } catch (error) {
      target = null;
    }
    if (!target) return;
    event.preventDefault();
    theme.scrollToElement(target);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    history.replaceState(null, '', link.hash);
  });

  const onReady = () => {
    setViewportVars();
    prepareExternalLinks();
    document.addEventListener('shopify:section:load', (event) => prepareExternalLinks(event.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
